import { ENTITIES } from 'src/models/QuerybuilderModel';
import { getEntitiesData, postDownloadEntitesData } from 'src/apis/QuerybuilderAPI';
import { SEARCH_OPERATORS_PRIMITIVE } from 'src/constants/SearchOperators';
import { PAYMENT_STATUS } from 'src/models/PaymentModel';
import { DELIVERY_TYPE, FORMAT, ORIENTATION } from 'src/models/DownloadModel';
import { DownloadOption } from 'src/lib/makeApiRequest';
import { normalizeForForm } from 'src/lib/QueryBuilderHelper';
import { INVOICE_PAYMENT_STATUS, INVOICE_STATE } from 'src/constants/Invoices';

export interface DashboardCardData {
  total: number;
  count: number;
  percentChange?: number;
  oldestDays?: number;
}

export interface DeclinedPaymentItem {
  id: string;
  paymentId: number;
  donor: string;
  amount: number;
  date: string;
  reason: string;
  paymentType: string;
  rawDate: string;
}

export interface UnpaidDonationItem {
  id: string;
  paymentId: number;
  donor: string;
  amount: number;
  dueDate: string;
  daysOverdue: number;
  rawDate: string;
}

export interface ScheduledPaymentItem {
  id: string;
  paymentId: number;
  donor: string;
  amount: number;
  scheduledDate: string;
  paymentMethod: string;
  rawDate: string;
}

const getTodayRange = () => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return [start.toISOString(), end.toISOString()];
};

const getYesterdayRange = () => {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const start = new Date(yesterday);
  start.setHours(0, 0, 0, 0);
  const end = new Date(yesterday);
  end.setHours(23, 59, 59, 999);
  return [start.toISOString(), end.toISOString()];
};

const getMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  return [start.toISOString(), end.toISOString()];
};

export type DownloadPeriod = 'all' | 'today' | 'lastWeek' | 'lastMonth' | 'tomorrow' | 'thisWeek' | 'month';

const buildDateFilter = (field: string, period: DownloadPeriod): any[] => {
  const now = new Date();
  const makeStart = () => { const d = new Date(now); d.setHours(0, 0, 0, 0); return d; };
  const makeEnd = () => { const d = new Date(now); d.setHours(23, 59, 59, 999); return d; };

  switch (period) {
    case 'today': {
      const start = makeStart();
      const end = makeEnd();
      return [
        [field, SEARCH_OPERATORS_PRIMITIVE.gte, start.toISOString()],
        [field, SEARCH_OPERATORS_PRIMITIVE.lte, end.toISOString()],
      ];
    }
    case 'lastWeek': {
      const start = makeStart();
      start.setDate(now.getDate() - 7);
      return [[field, SEARCH_OPERATORS_PRIMITIVE.gte, start.toISOString()]];
    }
    case 'lastMonth': {
      const start = makeStart();
      start.setDate(now.getDate() - 30);
      return [[field, SEARCH_OPERATORS_PRIMITIVE.gte, start.toISOString()]];
    }
    case 'tomorrow': {
      const tomorrow = new Date(now);
      tomorrow.setDate(now.getDate() + 1);
      const start = new Date(tomorrow);
      start.setHours(0, 0, 0, 0);
      const end = new Date(tomorrow);
      end.setHours(23, 59, 59, 999);
      return [
        [field, SEARCH_OPERATORS_PRIMITIVE.gte, start.toISOString()],
        [field, SEARCH_OPERATORS_PRIMITIVE.lte, end.toISOString()],
      ];
    }
    case 'thisWeek': {
      const end = new Date(now);
      end.setDate(now.getDate() + 7);
      return [
        [field, SEARCH_OPERATORS_PRIMITIVE.gte, now.toISOString()],
        [field, SEARCH_OPERATORS_PRIMITIVE.lte, end.toISOString()],
      ];
    }
    case 'month': {
      const [start, end] = getMonthRange();
      return [
        [field, SEARCH_OPERATORS_PRIMITIVE.gte, start],
        [field, SEARCH_OPERATORS_PRIMITIVE.lte, end],
      ];
    }
    default:
      return [];
  }
};

const normalizePayments = (response: any): any[] => {
  const list = response?.data?.result ?? [];
  return list.map((item: any) => normalizeForForm(item, ENTITIES.payments));
};

const getTotal = (response: any): number => normalizePayments(response).reduce(
  (sum: number, item: any) => sum + (Number(item.amount) || 0), 0,
);

const getCount = (response: any): number => {
  const list = response?.data?.result ?? [];
  return response?.data?.count ?? list.length;
};

export const downloadCollectedToday = async (organisation: string): Promise<void> => {
  const [todayStart, todayEnd] = getTodayRange();
  await postDownloadEntitesData(
    organisation,
    ENTITIES.payments,
    {
      filters: {
        and: [
          ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.gte, todayStart],
          ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.lte, todayEnd],
          ['payments.payment_status', SEARCH_OPERATORS_PRIMITIVE.eq, PAYMENT_STATUS.paid],
        ],
      },
    },
    {
      format: FORMAT.csv,
      delivery: DELIVERY_TYPE.download,
      orientation: ORIENTATION.landscape,
      file: 'collected-today',
      all: true,
    },
    DownloadOption.download,
  );
};

export const fetchCollectedToday = async (organisation: string): Promise<DashboardCardData> => {
  const [todayStart, todayEnd] = getTodayRange();
  const [yesterdayStart, yesterdayEnd] = getYesterdayRange();

  const [todayResponse, yesterdayResponse] = await Promise.all([
    getEntitiesData(organisation, ENTITIES.payments, {
      filters: {
        and: [
          ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.gte, todayStart],
          ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.lte, todayEnd],
          ['payments.payment_status', SEARCH_OPERATORS_PRIMITIVE.eq, PAYMENT_STATUS.paid],
        ],
      },
      pagination: { page: 1, size: 1000 },
    }),
    getEntitiesData(organisation, ENTITIES.payments, {
      filters: {
        and: [
          ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.gte, yesterdayStart],
          ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.lte, yesterdayEnd],
          ['payments.payment_status', SEARCH_OPERATORS_PRIMITIVE.eq, PAYMENT_STATUS.paid],
        ],
      },
      pagination: { page: 1, size: 1000 },
    }),
  ]);

  const todayTotal = todayResponse.success ? getTotal(todayResponse) : 0;
  const count = getCount(todayResponse);
  const yesterdayTotal = yesterdayResponse.success ? getTotal(yesterdayResponse) : 0;

  const percentChange = yesterdayTotal > 0
    ? Math.round(((todayTotal - yesterdayTotal) / yesterdayTotal) * 100)
    : 0;

  return { total: todayTotal, count, percentChange };
};

export const fetchDeclinedPayments = async (organisation: string): Promise<DashboardCardData> => {
  const response = await getEntitiesData(organisation, ENTITIES.payments, {
    filters: {
      and: [
        ['payments.payment_status', SEARCH_OPERATORS_PRIMITIVE.eq, PAYMENT_STATUS.declined],
      ],
    },
    pagination: { page: 1, size: 1000 },
  });
  return response.success
    ? { total: getTotal(response), count: getCount(response) }
    : { total: 0, count: 0 };
};

export const fetchUnpaidDonations = async (organisation: string): Promise<DashboardCardData> => {
  const response = await getEntitiesData(organisation, ENTITIES.invoices, {
    filters: {
      and: [
        ['invoices.payment_status', SEARCH_OPERATORS_PRIMITIVE.in,
          [INVOICE_PAYMENT_STATUS.not_paid, INVOICE_PAYMENT_STATUS.partially_paid],
        ],
        ['invoices.state', SEARCH_OPERATORS_PRIMITIVE.eq, INVOICE_STATE.final],
        ['invoices.balance', SEARCH_OPERATORS_PRIMITIVE.gt, '0'],
      ],
    },
    pagination: { page: 1, size: 1000 },
  });
  if (!response.success) return { total: 0, count: 0 };

  const list = (response?.data?.result ?? []).map(
    (item: any) => normalizeForForm(item, ENTITIES.invoices),
  );
  const total = list.reduce((sum: number, item: any) => sum + (Number(item.balance) || 0), 0);
  const count = response?.data?.count ?? list.length;

  const now = new Date();
  let oldestDays = 0;
  list.forEach((item: any) => {
    if (item.due_date) {
      const dueDate = new Date(item.due_date);
      const diffDays = Math.ceil((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays > oldestDays) oldestDays = diffDays;
    }
  });

  return { total, count, oldestDays };
};

export const fetchScheduledPayments = async (organisation: string): Promise<DashboardCardData> => {
  const [start, end] = getMonthRange();
  const response = await getEntitiesData(organisation, ENTITIES.payments, {
    filters: {
      and: [
        ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.gte, start],
        ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.lte, end],
        ['payments.payment_status', SEARCH_OPERATORS_PRIMITIVE.eq, PAYMENT_STATUS.scheduled],
      ],
    },
    pagination: { page: 1, size: 1000 },
  });
  return response.success
    ? { total: getTotal(response), count: getCount(response) }
    : { total: 0, count: 0 };
};

export const fetchDeclinedPaymentsList = async (
  organisation: string,
): Promise<DeclinedPaymentItem[]> => {
  const response = await getEntitiesData(organisation, ENTITIES.payments, {
    filters: {
      and: [
        ['payments.payment_status', SEARCH_OPERATORS_PRIMITIVE.eq, PAYMENT_STATUS.declined],
      ],
    },
    pagination: { page: 1, size: 1000 },
  });
  return normalizePayments(response).map((item: any) => ({
    id: String(item.id),
    paymentId: Number(item.id),
    donor: [item.contact_first_name, item.contact_last_name]
      .filter(Boolean).join(' ') || item.contact_company_name || '-',
    amount: Number(item.amount) || 0,
    date: item.scheduled_date
      ? new Date(item.scheduled_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : '-',
    reason: item.result_message ?? item.result_code ?? '-',
    paymentType: item.payment_type ?? '',
    rawDate: item.scheduled_date ?? '',
  }));
};

export const fetchUnpaidDonationsList = async (
  organisation: string,
): Promise<UnpaidDonationItem[]> => {
  const response = await getEntitiesData(organisation, ENTITIES.invoices, {
    filters: {
      and: [
        ['invoices.payment_status', SEARCH_OPERATORS_PRIMITIVE.in,
          [INVOICE_PAYMENT_STATUS.not_paid, INVOICE_PAYMENT_STATUS.partially_paid],
        ],
        ['invoices.state', SEARCH_OPERATORS_PRIMITIVE.eq, INVOICE_STATE.final],
        ['invoices.balance', SEARCH_OPERATORS_PRIMITIVE.gt, '0'],
      ],
    },
    pagination: { page: 1, size: 1000 },
  });
  const list = (response?.data?.result ?? []).map(
    (item: any) => normalizeForForm(item, ENTITIES.invoices),
  );
  const now = new Date();
  return list.map((item: any) => {
    const dueDate = item.due_date ? new Date(item.due_date) : null;
    const daysOverdue = dueDate
      ? Math.ceil((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))
      : 0;
    return {
      id: item.reference ?? `INV-${item.id}`,
      paymentId: Number(item.id),
      donor: [item.contact_first_name, item.contact_last_name]
        .filter(Boolean).join(' ') || item.contact_company_name || '-',
      amount: Number(item.balance) || 0,
      dueDate: dueDate
        ? dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : '-',
      daysOverdue,
      rawDate: item.due_date ?? '',
    };
  }).sort((a: UnpaidDonationItem, b: UnpaidDonationItem) => b.daysOverdue - a.daysOverdue);
};

export const downloadUnpaidDonations = async (
  organisation: string,
  period: DownloadPeriod = 'all',
): Promise<void> => {
  const dateFilters = buildDateFilter('invoices.due_date', period);
  await postDownloadEntitesData(
    organisation,
    ENTITIES.invoices,
    {
      filters: {
        and: [
          ['invoices.payment_status', SEARCH_OPERATORS_PRIMITIVE.in,
            [INVOICE_PAYMENT_STATUS.not_paid, INVOICE_PAYMENT_STATUS.partially_paid],
          ],
          ['invoices.state', SEARCH_OPERATORS_PRIMITIVE.eq, INVOICE_STATE.final],
          ['invoices.balance', SEARCH_OPERATORS_PRIMITIVE.gt, '0'],
          ...dateFilters,
        ],
      },
    },
    {
      format: FORMAT.csv,
      delivery: DELIVERY_TYPE.download,
      orientation: ORIENTATION.landscape,
      file: 'unpaid-donations',
      all: true,
    },
    DownloadOption.download,
  );
};

export const downloadDeclinedPayments = async (
  organisation: string,
  period: DownloadPeriod = 'all',
): Promise<void> => {
  const dateFilters = buildDateFilter('payments.scheduled_date', period);
  await postDownloadEntitesData(
    organisation,
    ENTITIES.payments,
    {
      filters: {
        and: [
          ['payments.payment_status', SEARCH_OPERATORS_PRIMITIVE.eq, PAYMENT_STATUS.declined],
          ...dateFilters,
        ],
      },
    },
    {
      format: FORMAT.csv,
      delivery: DELIVERY_TYPE.download,
      orientation: ORIENTATION.landscape,
      file: 'declined-payments',
      all: true,
    },
    DownloadOption.download,
  );
};

export const fetchScheduledPaymentsList = async (
  organisation: string,
): Promise<ScheduledPaymentItem[]> => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 2, 0, 23, 59, 59, 999);
  const response = await getEntitiesData(organisation, ENTITIES.payments, {
    filters: {
      and: [
        ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.gte, start.toISOString()],
        ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.lte, end.toISOString()],
        ['payments.payment_status', SEARCH_OPERATORS_PRIMITIVE.eq, PAYMENT_STATUS.scheduled],
      ],
    },
    pagination: { page: 1, size: 1000 },
  });
  return normalizePayments(response).map((item: any) => ({
    id: String(item.id),
    paymentId: Number(item.id),
    donor: [item.contact_first_name, item.contact_last_name]
      .filter(Boolean).join(' ') || item.contact_company_name || '-',
    amount: Number(item.amount) || 0,
    scheduledDate: item.scheduled_date
      ? new Date(item.scheduled_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : '-',
    paymentMethod: item.payment_type ?? '-',
    rawDate: item.scheduled_date ?? '',
  }));
};

export const downloadScheduledPayments = async (
  organisation: string,
  period: DownloadPeriod = 'all',
): Promise<void> => {
  const dateFilters = buildDateFilter('payments.scheduled_date', period);
  if (dateFilters.length === 0) {
    const [start, end] = getMonthRange();
    dateFilters.push(
      ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.gte, start],
      ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.lte, end],
    );
  }
  await postDownloadEntitesData(
    organisation,
    ENTITIES.payments,
    {
      filters: {
        and: [
          ...dateFilters,
          ['payments.payment_status', SEARCH_OPERATORS_PRIMITIVE.eq, PAYMENT_STATUS.scheduled],
        ],
      },
    },
    {
      format: FORMAT.csv,
      delivery: DELIVERY_TYPE.download,
      orientation: ORIENTATION.landscape,
      file: 'scheduled-payments',
      all: true,
    },
    DownloadOption.download,
  );
};

export interface RecentPaymentItem {
  id: string;
  paymentId: number;
  donor: string;
  amount: number;
  date: string;
  method: string;
  rawDate: string;
}

export const downloadRecentPayments = async (
  organisation: string,
  period: DownloadPeriod = 'all',
): Promise<void> => {
  const dateFilters = buildDateFilter('payments.scheduled_date', period);
  if (dateFilters.length === 0) {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - 30);
    start.setHours(0, 0, 0, 0);
    dateFilters.push(
      ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.gte, start.toISOString()],
    );
  }
  await postDownloadEntitesData(
    organisation,
    ENTITIES.payments,
    {
      filters: {
        and: [
          ...dateFilters,
          ['payments.payment_status', SEARCH_OPERATORS_PRIMITIVE.eq, PAYMENT_STATUS.paid],
        ],
      },
    },
    {
      format: FORMAT.csv,
      delivery: DELIVERY_TYPE.download,
      orientation: ORIENTATION.landscape,
      file: 'recent-payments',
      all: true,
    },
    DownloadOption.download,
  );
};

export const fetchRecentPaymentsList = async (
  organisation: string,
): Promise<RecentPaymentItem[]> => {
  const now = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - 30);
  start.setHours(0, 0, 0, 0);
  const response = await getEntitiesData(organisation, ENTITIES.payments, {
    filters: {
      and: [
        ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.gte, start.toISOString()],
        ['payments.payment_status', SEARCH_OPERATORS_PRIMITIVE.eq, PAYMENT_STATUS.paid],
      ],
    },
    pagination: { page: 1, size: 1000 },
  });
  return normalizePayments(response).map((item: any) => ({
    id: String(item.id),
    paymentId: Number(item.id),
    donor: [item.contact_first_name, item.contact_last_name]
      .filter(Boolean).join(' ') || item.contact_company_name || '-',
    amount: Number(item.amount) || 0,
    date: item.scheduled_date
      ? new Date(item.scheduled_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : '-',
    method: item.payment_type ?? '-',
    rawDate: item.scheduled_date ?? '',
  }));
};

export interface EventItem {
  id: string;
  eventId: number;
  name: string;
  raised: number;
  goal: number;
  endDate: string;
  url: string;
}

export const fetchEventsList = async (
  organisation: string,
): Promise<EventItem[]> => {
  const response = await getEntitiesData(organisation, ENTITIES.events, {
    pagination: { page: 1, size: 1000 },
  });
  const list = (response?.data?.result ?? []).map(
    (item: any) => normalizeForForm(item, ENTITIES.events),
  );
  return list.map((item: any) => ({
    id: String(item.id),
    eventId: Number(item.id),
    name: item.name ?? '-',
    raised: Number(item.raised) || 0,
    goal: Number(item.goal) || 0,
    endDate: item.end_date
      ? new Date(item.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : '-',
    url: item.slug
      ? `https://www.perfectdeed.ca/events/${item.slug}`
      : '',
  }));
};

export interface TopDonorItem {
  donor: string;
  total: number;
  gifts: number;
  contactId: number;
}

export const fetchTopDonors = async (
  organisation: string,
): Promise<TopDonorItem[]> => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const response = await getEntitiesData(organisation, ENTITIES.payments, {
    filters: {
      and: [
        ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.gte, start.toISOString()],
        ['payments.payment_status', SEARCH_OPERATORS_PRIMITIVE.eq, PAYMENT_STATUS.paid],
      ],
    },
    pagination: { page: 1, size: 1000 },
  });
  const payments = normalizePayments(response);
  const byDonor: Record<string, TopDonorItem> = {};
  payments.forEach((item: any) => {
    const name = [item.contact_first_name, item.contact_last_name]
      .filter(Boolean).join(' ') || item.contact_company_name || '-';
    if (!byDonor[name]) {
      byDonor[name] = {
        donor: name,
        total: 0,
        gifts: 0,
        contactId: Number(item.contact_id) || 0,
      };
    }
    byDonor[name].total += Number(item.amount) || 0;
    byDonor[name].gifts += 1;
  });
  return Object.values(byDonor)
    .sort((a, b) => b.total - a.total)
    .slice(0, 10);
};

export const fetchEvents = async (organisation: string): Promise<DashboardCardData> => {
  const response = await getEntitiesData(organisation, ENTITIES.events, {
    pagination: { page: 1, size: 1000 },
  });
  const list = (response?.data?.result ?? []).map(
    (item: any) => normalizeForForm(item, ENTITIES.events),
  );
  return response.success
    ? {
      total: list.reduce((sum: number, item: any) => sum + (Number(item.raised) || 0), 0),
      count: response?.data?.count ?? list.length,
    }
    : { total: 0, count: 0 };
};
